/**
 * Hard-Copy Document Tracking Types
 * Types for offline/physical document lifecycle management.
 */

export type HardCopyTrackingStatus =
  | 'generated'
  | 'printed'
  | 'provided_for_signature'
  | 'signed'
  | 'uploaded_signed_copy'
  | 'cancelled';

export interface HardCopyTrackingRecord {
  id: number;
  uuid: string;
  cbo_id: number;
  tracking_number: string;
  doc_record_id: number | null;
  form_instance_id: number | null;
  target_type: 'member' | 'staff';
  target_id: number;
  target_name: string | null;
  program_id: number | null;
  program_name: string | null;
  enrollment_id: number | null;
  status: HardCopyTrackingStatus;

  generated_at: string | null;
  generated_by: number | null;
  generated_by_name: string | null;

  printed_at: string | null;
  printed_by: number | null;
  printed_by_name: string | null;

  provided_at: string | null;
  provided_by: number | null;
  provided_by_name: string | null;
  provided_notes: string | null;

  member_signed_at: string | null;
  signed_recorded_at: string | null;
  signed_recorded_by: number | null;
  signed_recorded_by_name: string | null;

  signed_copy_doc_file_id: number | null;
  signed_copy_uploaded_at: string | null;
  signed_copy_uploaded_by: number | null;
  signed_copy_uploaded_by_name: string | null;

  cancelled_at: string | null;
  cancelled_by: number | null;
  cancellation_reason: string | null;

  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateTrackingRequest {
  doc_record_id?: number;
  form_instance_id?: number;
  target_type: 'member' | 'staff';
  target_id: number;
  program_id?: number;
  enrollment_id?: number;
  notes?: string;
}

export interface PrintRecord {
  notes?: string;
}

export interface ProvideRecord {
  provided_date?: string;
  notes?: string;
}

export interface SignatureRecord {
  member_signed_at: string; // YYYY-MM-DD
  notes?: string;
}

export interface CancelRecord {
  reason: string;
}

export interface OrgTrackingSettings {
  prefix: string;
  next_number: number;
}

export interface TrackingAuditEvent {
  id: number;
  action: string;
  performed_by: number;
  performed_by_name: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

export interface TrackingHistoryResponse {
  tracking_id: number;
  tracking_number: string;
  events: TrackingAuditEvent[];
}

/**
 * Status display configuration for UI rendering.
 */
export const TRACKING_STATUS_CONFIG: Record<
  HardCopyTrackingStatus,
  { label: string; color: string; badgeVariant: string; step: number }
> = {
  generated: { label: 'Generated', color: 'bg-gray-100 text-gray-700', badgeVariant: 'default', step: 0 },
  printed: { label: 'Printed', color: 'bg-blue-100 text-blue-700', badgeVariant: 'primary', step: 1 },
  provided_for_signature: { label: 'Provided for Signature', color: 'bg-amber-100 text-amber-700', badgeVariant: 'warning', step: 2 },
  signed: { label: 'Signed', color: 'bg-purple-100 text-purple-700', badgeVariant: 'secondary', step: 3 },
  uploaded_signed_copy: { label: 'Signed Copy Uploaded', color: 'bg-green-100 text-green-700', badgeVariant: 'success', step: 4 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', badgeVariant: 'danger', step: -1 },
};

export const LIFECYCLE_STEPS: { key: HardCopyTrackingStatus; label: string }[] = [
  { key: 'generated', label: 'Generated' },
  { key: 'printed', label: 'Printed' },
  { key: 'provided_for_signature', label: 'Provided' },
  { key: 'signed', label: 'Signed' },
  { key: 'uploaded_signed_copy', label: 'Uploaded' },
];
